import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { type MultiFactorError } from 'firebase/auth';
import { AuthService } from './auth.service';
import { MultifactorService } from './multifactor/multifactor.service';
import { MULTIFACTOR_VERIFY_ROUTE } from './multifactor/multifactor.types';

export type AuthSignInErrorHandlingResult = 'handled' | 'unhandled';

/**
 * Handles errors thrown during Firebase sign-in by distinguishing
 * multi-factor authentication challenges from general auth failures.
 *
 * When the error is a `MultiFactorError`, the service resolves the
 * MFA sign-in context, stores it on {@link AuthService}, and navigates
 * to the MFA verification route. A concurrency guard prevents duplicate
 * navigations if multiple MFA errors fire in quick succession.
 *
 * All other errors are surfaced to the user via a snackbar and returned
 * as `'unhandled'` so callers can apply additional error handling.
 *
 * Provided at the component level by `LoginComponent` and `SignInComponent`.
 */
@Injectable()
export class AuthSignInErrorHandlerService {
  private _multifactorNavigationInProgress = false;
  private _router = inject(Router);
  private _snackBar = inject(MatSnackBar);
  private _auth = inject(AuthService);
  private _multifactorService = inject(MultifactorService);

  async handle(event: Error): Promise<AuthSignInErrorHandlingResult> {
    if (!this._isMultifactorRequired(event)) {
      this._snackBar.open(event.message);
      return 'unhandled';
    }

    if (this._multifactorNavigationInProgress) {
      return 'handled';
    }

    this._multifactorNavigationInProgress = true;
    this._snackBar.dismiss();

    try {
      const context = this._multifactorService.resolveMultiFactorSignIn(event);
      this._auth.setMultifactorVerificationContext(context);
      const navigated = await this._router.navigate([MULTIFACTOR_VERIFY_ROUTE]);
      if (!navigated) {
        this._multifactorNavigationInProgress = false;
        this._snackBar.open(
          'Multi-factor authentication error. Please try again.'
        );
        return 'unhandled';
      }
    } catch {
      this._snackBar.open(
        'Multi-factor authentication error. Please try again.'
      );
      this._multifactorNavigationInProgress = false;
      return 'unhandled';
    }

    return 'handled';
  }

  private _isMultifactorRequired(event: Error): event is MultiFactorError {
    return (
      (event as MultiFactorError).code === 'auth/multi-factor-auth-required'
    );
  }
}
